import { useAuth as useWorkOsAuth } from '@workos-inc/authkit-react';
import { isEmpty } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { signOutUser } from 'src/api/authApi';
import { setOrgCookie } from 'src/api/organizationApi';
import { getCurrentUser } from 'src/api/userApi';
import { useLocalLocation } from 'src/hooks/useLocalLocation';
import { orgAction } from 'src/redux/actions';
import {
   ORGANIZATION_KEY,
   permissionKeys,
   RECENT_SEARCHES_MIGRATED_KEY,
   routePaths,
   TOKEN_KEY,
   USER_KEY,
   WORKOS_USER_KEY,
} from 'src/utils/constants';
import { upsertRecentSearch } from 'src/api/recentSearchApi';
import { clearLocalRecentSearches, readLocalRecentSearches } from 'src/utils/recentSearchStorage';
import { useQueryClient } from '@tanstack/react-query';
import { RecentSearchQueryKeys } from 'src/hooks/useRecentSearches';
import { deleteCookie, getCookie, setCookie } from 'src/utils/cookie.util';
import { UserType } from 'src/utils/proptypes';
import { getUserPermissions, hasPermissions } from 'src/utils/role-permission.util';
import { getCurrentOrgRolePermissions } from 'src/acl/ability';
import { clearLocalStorage, getLocalStorage, getLocalStorageUser, removeLocalStorage, setLocalStorage } from 'src/utils/storage.util';
import * as Sentry from '@sentry/react';
import { identifyUser, resetUser, setOrganizationContext } from 'src/analytics/mixpanel';
import { track as rawTrack } from 'src/analytics/mixpanel';
import { AnalyticsEvents } from 'src/analytics/events';
import { type AuthHandoffContext, clearAuthHandoff, completeAuthHandoff, startAuthHandoff } from 'src/analytics/authHandoff';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { leaveForAuthProvider } from 'src/utils/authRedirect.util';

const AUTH_PENDING_KEY = 'npt_auth_pending';

// The org id is stored as a bare string (handleSetCookie), which getLocalStorage's JSON.parse
// rejects, so the remembered org was never found and every login fell back to the first one.
const readStoredOrganizationId = (): string | null => {
   try {
      return localStorage.getItem(ORGANIZATION_KEY) || null;
   } catch {
      return null;
   }
};

/**
 * WorkOS OAuth providers that can be deep-linked via `provider` option on
 * signIn/signUp. Bypasses WorkOS's "pick a method" screen and sends the
 * user straight to the provider's OAuth flow. Add more as needed.
 *
 * Reference: https://workos.com/docs/user-management/1-configure-your-project/configure-a-redirect-uri
 */
export type WorkOsOAuthProvider = 'GoogleOAuth' | 'MicrosoftOAuth' | 'AppleOAuth' | 'GitHubOAuth';

export interface AuthRedirectOptions {
   returnTo?: string;
   /**
    * When set, deep-link to the provider's OAuth flow rather than the
    * WorkOS-hosted "pick a method" screen. Because the authkit-js
    * `getAuthorizationUrl` helper strips the `provider` param, we
    * generate the URL via authkit and then append `&provider=<name>`
    * manually before redirecting — WorkOS's authorize endpoint accepts
    * the extra param and routes to the requested provider.
    */
   provider?: WorkOsOAuthProvider;
   /** Which button sent the traveler to WorkOS, for the auth_handoff_* events (NPT-1694). */
   handoff?: AuthHandoffContext;
   /** NPT-2091 — prefill the email on the WorkOS page (a traveler claiming an admin-made account). */
   loginHint?: string;
}

type AuthContextType = {
   user: UserType | null;
   isAuthenticated: boolean;
   isAdmin: boolean;
   isDisabled: boolean;
   isLoading: boolean;
   isAuthRedirecting: boolean;
   /**
    * NPT-2091 — AuthKit has finished starting up. Until then getSignIn/SignUpUrl return '' and
    * signIn/signUp silently do nothing, so a page that redirects on load must wait for this.
    */
   isAuthProviderReady: boolean;
   /**
    * NPT-2144 — admin of the current org (READ_ORG through their role there) and not a platform
    * admin. Org admins land on /admin/org and see only their org tree.
    */
   isOrgAdmin: boolean;
   /** NPT-2144 — the org whose `organization_id` cookie is set: the org admin API's `:organizationId`. */
   currentOrganizationId: string | null;
   refreshUser: () => Promise<any>;
   signIn: (options?: AuthRedirectOptions) => void;
   signUp: (options?: AuthRedirectOptions) => void;
   signOut: (options?: { returnTo?: string; navigate: false }) => Promise<void>;
   setUser: (user: UserType | null) => void;
};

const AuthContext = createContext<AuthContextType>({
   user: null,
   isAuthenticated: false,
   isAdmin: false,
   isDisabled: false,
   isLoading: false,
   isAuthRedirecting: false,
   isAuthProviderReady: false,
   isOrgAdmin: false,
   currentOrganizationId: null,
   signIn: () => {},
   signUp: () => {},
   signOut: async () => {},
   setUser: () => {},
   refreshUser: async () => {},
});

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
   const workosAuth = useWorkOsAuth();
   const { currentPath } = useLocalLocation();
   // useLDClient returns `undefined` when no LDProvider is mounted above
   // (e.g. VITE_LD_CLIENT_ID missing). All downstream identify() calls
   // guard against this, so no branching needed at the callsite.
   const ldClient = useLDClient();

   const dispatch = useDispatch();
   const navigate = useNavigate();
   const queryClient = useQueryClient();

   const token = (getCookie(TOKEN_KEY) as string) || null;

   const [user, setUser] = useState<UserType | null>((getLocalStorageUser() as UserType) || null);

   const [isLoading, setIsLoading] = useState(true);
   const [isDisabled, setIsDisabled] = useState(false);
   const [isAuthRedirecting, setIsAuthRedirecting] = useState(false);
   const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(readStoredOrganizationId);

   /**
    * NPT-2099: set once the traveler signs out on this page. While it is set, a WorkOS user that
    * AuthKit restores (its session refresh can land after our signOut) must not log them back in.
    * `pendingWorkOsSignOutRef` marks a signOut that ran before AuthKit had its real client, so the
    * WorkOS session still needs ending once AuthKit finishes loading.
    */
   const signedOutRef = useRef(false);
   const pendingWorkOsSignOutRef = useRef(false);

   let isFetchingUser = false;

   const fetchCurrentUser = async () => {
      let userResponse = null;

      if (isFetchingUser) return Promise.resolve(null);

      isFetchingUser = true;

      try {
         userResponse = await getCurrentUser();
         if (userResponse.data?.id) {
            // Store current user
            setUser(userResponse.data);

            setLocalStorage(USER_KEY, userResponse.data);
            setIsDisabled(false);

            // Store org info
            const { organizations } = userResponse.data;
            const organizationDefault = readStoredOrganizationId();

            if (!organizations || isEmpty(organizations)) {
               removeLocalStorage(ORGANIZATION_KEY);
               setCurrentOrganizationId(null);
            } else if (!organizationDefault || !organizations.find((o: any) => o.id === organizationDefault)) {
               await handleSetCookie(organizations[0].id);
            } else {
               await handleSetCookie(organizationDefault);
            }
         } else {
            clearUserSession();
         }
      } catch (err: any) {
         if (err.response?.data?.errors?.length) {
            const error = err.response?.data?.errors[0];
            if (error.type === 'ACCOUNT_DISABLED') {
               setIsDisabled(true);

               navigate(routePaths.ACCOUNT_DISABLED);
            }
         }
      }

      isFetchingUser = false;

      return userResponse;
   };

   useEffect(() => {
      const validateToken = async () => {
         setIsLoading(true);

         try {
            if (workosAuth.user && signedOutRef.current) {
               // Signed out on this page: ignore the WorkOS session AuthKit restored (NPT-2099).
            } else if (workosAuth.user) {
               setLocalStorage(WORKOS_USER_KEY, workosAuth.user);

               const workosToken = await workosAuth.getAccessToken();
               if (workosToken) {
                  // Set cookie and expired in 1 day. To change if update in WorkOS
                  setCookie(TOKEN_KEY, workosToken, 1440);

                  await fetchCurrentUser();
               } else {
                  clearUserSession();
               }
            } else if (token) {
               // If local token exists, get current user
               await fetchCurrentUser();
            }
         } catch (err: any) {
            console.log(err);
         }

         setIsLoading(false);
         setIsAuthRedirecting(false);
      };

      validateToken();
   }, [token, workosAuth, workosAuth.user]);

   // Analytics: once the user is resolved, stitch the anonymous session to
   // the user UUID and set People properties. Runs whenever the user id
   // changes, so both fresh logins and session restores are covered.
   useEffect(() => {
      if (!user?.id) return;
      // Sentry issues were anonymous: `sendDefaultPii` attaches an IP, not an
      // identity, so nobody could tell whether one user hit an error fifty
      // times or fifty users hit it once.
      const email = (user as any).emailAddress;
      Sentry.setUser({ id: user.id, email });
      identifyUser({
         userId: user.id,
         email,
         firstName: (user as any).firstName,
         lastName: (user as any).lastName,
         phoneNumber: (user as any).phoneNumber,
         createdAt: (user as any).created,
      });

      // Fire signup_completed when the user just returned from a WorkOS
      // redirect (signIn or signUp). The sessionStorage flag distinguishes
      // fresh auth flows from session restores on page reload. Super
      // properties (last_experiment_key, last_experiment_variant, ld_*)
      // carry through automatically so the event is fully attributed.
      try {
         const authPending = sessionStorage.getItem(AUTH_PENDING_KEY);
         if (authPending) {
            sessionStorage.removeItem(AUTH_PENDING_KEY);
            rawTrack(AnalyticsEvents.SIGNUP_COMPLETED, {
               auth_method: authPending,
            });
         }
      } catch { /* private mode */ }
      // If the user belongs to an org but hasn't landed on an org-scoped
      // page yet, fall back to their primary org so events are still
      // tagged. Landing/org-microsite pages will overwrite this once
      // they dispatch `orgAction.setOrganization`.
      const primaryOrg = (user as any).organizations?.[0];
      if (primaryOrg?.id) {
         setOrganizationContext({
            id: primaryOrg.id,
            slug: primaryOrg.slug,
            name: primaryOrg.name,
         });
      }

      // LaunchDarkly: hand the authenticated user-id to LD so any
      // experiment rules targeting user attributes (role, org, etc.)
      // evaluate correctly. Sticky bucketing across anon→auth is
      // preserved because LD's `identify()` can transfer the anonymous
      // context's bucketing history when the same key style is used —
      // but we intentionally don't rely on that here: these experiments
      // force `control` for authenticated users via the hook's
      // short-circuit, so post-login bucket flips are a non-issue.
      if (ldClient) {
         ldClient
            .identify({
               kind: 'user',
               key: user.id,
               anonymous: false,
               email: (user as any).emailAddress,
               organizationId: primaryOrg?.id,
               organizationSlug: primaryOrg?.slug,
            })
            .catch((err: unknown) => {
               console.warn('[LD] identify failed', err);
            });
      }

      try {
         if (window.localStorage.getItem(RECENT_SEARCHES_MIGRATED_KEY)) return;
         const local = readLocalRecentSearches();
         if (local.length === 0) {
            window.localStorage.setItem(RECENT_SEARCHES_MIGRATED_KEY, '1');
            return;
         }
         (async () => {
            // Send oldest first so the most-recent local search ends up with
            // the latest lastSearchedAt server-side and stays at the top.
            const ordered = [...local].sort((a, b) => (a.lastSearchedAt < b.lastSearchedAt ? -1 : 1));
            for (const item of ordered) {
               try {
                  await upsertRecentSearch({
                     organizationId: item.organizationId,
                     mapboxId: item.mapboxId,
                     placeName: item.placeName,
                     center: item.center,
                     mapboxObject: item.mapboxObject,
                     checkin: item.checkin,
                     checkout: item.checkout,
                     adults: item.adults,
                     children: item.children,
                     rooms: item.rooms,
                     yearOlds: item.yearOlds,
                     isPet: item.isPet,
                     countryCode: item.countryCode,
                  });
               } catch (err) {
                  console.warn('[recentSearches] migration failed for one item', err);
               }
            }
            clearLocalRecentSearches();
            window.localStorage.setItem(RECENT_SEARCHES_MIGRATED_KEY, '1');
            queryClient.invalidateQueries({ queryKey: [RecentSearchQueryKeys.List] });
         })();
      } catch (err) {
         console.warn('[recentSearches] migration aborted', err);
      }
   }, [user?.id, ldClient]);

   // NPT-2099: a signOut that ran while AuthKit was still loading only had its placeholder client,
   // whose signOut does nothing. End the WorkOS session now that the real client is here.
   const { isLoading: isWorkOsLoading, user: workOsUser, signOut: workOsSignOut } = workosAuth;
   useEffect(() => {
      if (isWorkOsLoading || !pendingWorkOsSignOutRef.current) return;
      pendingWorkOsSignOutRef.current = false;
      if (!workOsUser) return;
      (async () => {
         try {
            await workOsSignOut();
         } catch (err) {
            console.error('[Auth] Deferred WorkOS signOut failed', err);
         }
      })();
   }, [isWorkOsLoading, workOsUser, workOsSignOut]);

   // NPT-1694: close the auth handoff that signIn/signUp opened, once both our session check and
   // WorkOS's code exchange have finished — while WorkOS is still loading, a traveler who did
   // sign in would read as abandoned.
   useEffect(() => {
      if (isLoading || workosAuth.isLoading) return;
      completeAuthHandoff({ authenticated: !!workosAuth.user || !!getCookie(TOKEN_KEY) });
   }, [isLoading, workosAuth.isLoading, workosAuth.user]);

   // Back from WorkOS can restore the page from the back-forward cache (Safari especially): no
   // reload and no effects, only `pageshow` says the traveler is here again.
   useEffect(() => {
      const onPageShow = (event: PageTransitionEvent) => {
         if (event.persisted) completeAuthHandoff({ authenticated: !!getCookie(TOKEN_KEY), restoredFromCache: true });
      };
      window.addEventListener('pageshow', onPageShow);
      return () => window.removeEventListener('pageshow', onPageShow);
   }, []);

   /**
    * authkit's own `signIn`/`signUp` push the WorkOS page onto history
    * (`location.assign`), so we build the URL with `getSignIn/SignUpUrl`
    * (which also sets up PKCE in sessionStorage) and leave through
    * `leaveForAuthProvider`, which keeps the detour out of the back stack
    * (NPT-1971).
    *
    * With `provider`, we deep-link to that OAuth flow instead of the
    * WorkOS "pick a method" screen: authkit's RedirectOptions omit
    * `provider`, but the WorkOS authorize endpoint accepts it. REPLACE the
    * default `provider=authkit` param — a second `provider=` would leave
    * both in the URL and WorkOS would reject (or pick the first, stripping
    * our deep-link). Also drop `screen_hint` — WorkOS rejects it when
    * `provider` is not `authkit`.
    */
   const redirectToWorkOs = async (kind: 'signIn' | 'signUp', returnTo?: string, provider?: WorkOsOAuthProvider, loginHint?: string) => {
      const stateOpt = returnTo || loginHint ? { ...(returnTo && { state: { returnTo } }), ...(loginHint && { loginHint }) } : undefined;
      const baseUrl = kind === 'signUp' ? await workosAuth.getSignUpUrl(stateOpt) : await workosAuth.getSignInUrl(stateOpt);
      // authkit's placeholder client, used until it finishes initialising, hands back an empty URL.
      if (!baseUrl) return;
      const url = new URL(baseUrl);
      if (provider) {
         url.searchParams.set('provider', provider);
         url.searchParams.delete('screen_hint');
      }
      leaveForAuthProvider(url.toString());
   };

   // For login & redirect
   useEffect(() => {
      if (window.location.pathname.includes(routePaths.LOGIN_PAGE)) {
         setIsAuthRedirecting(true);
         startAuthHandoff('sign_in', undefined, { trigger: 'login_page' });
         redirectToWorkOs('signIn', !user && currentPath ? currentPath : undefined);
      }
   }, [window.location, workosAuth.signIn]);

   const isAuthenticated = !!user;
   const isAdmin = (user && hasPermissions(permissionKeys.PLATFORM_ADMIN, getUserPermissions(user))) || false;
   const isOrgAdmin = (!!user && !isAdmin && getCurrentOrgRolePermissions(user, currentOrganizationId).includes(permissionKeys.READ_ORG)) || false;

   const signIn = async (options?: AuthRedirectOptions) => {
      signedOutRef.current = false;
      pendingWorkOsSignOutRef.current = false;
      setIsLoading(true);
      setIsAuthRedirecting(true);
      try { sessionStorage.setItem(AUTH_PENDING_KEY, 'login'); } catch { /* private mode */ }

      const redirectUri = options?.returnTo || currentPath || '';
      const notRedirectUris = [routePaths.LANDING, routePaths.LOGIN_PAGE];
      // Make sure convert the redirectUri to pathname
      const pathname = new URL(redirectUri, window.location.origin).pathname;
      const shouldPassReturnTo = redirectUri && !notRedirectUris.includes(pathname);
      const effectiveReturnTo = shouldPassReturnTo ? redirectUri.trim() : undefined;

      startAuthHandoff('sign_in', options?.provider, options?.handoff);
      try {
         await redirectToWorkOs('signIn', effectiveReturnTo, options?.provider, options?.loginHint);
      } catch (error) {
         clearAuthHandoff();
         setIsAuthRedirecting(false);
         setIsLoading(false);
         throw error;
      }
   };

   const signUp = async (options?: AuthRedirectOptions) => {
      signedOutRef.current = false;
      pendingWorkOsSignOutRef.current = false;
      setIsLoading(true);
      setIsAuthRedirecting(true);
      try { sessionStorage.setItem(AUTH_PENDING_KEY, 'signup'); } catch { /* private mode */ }

      const redirectUri = options?.returnTo || currentPath;
      const shouldPassReturnTo = redirectUri && !redirectUri.includes(routePaths.REGISTER_PAGE) && redirectUri.trim() !== '/';
      const effectiveReturnTo = shouldPassReturnTo ? redirectUri.trim() : undefined;

      startAuthHandoff('sign_up', options?.provider, options?.handoff);
      try {
         await redirectToWorkOs('signUp', effectiveReturnTo, options?.provider, options?.loginHint);
      } catch (error) {
         clearAuthHandoff();
         setIsAuthRedirecting(false);
         setIsLoading(false);
         throw error;
      }
   };

   // Memoized so `logout.tsx`'s effect (deps: [signOut, navigate]) doesn't refire on
   // every AuthProvider render — an unmemoized signOut was recreated each render,
   // re-triggering the effect's navigate(replace: true) in a loop and tripping
   // history.replaceState()'s browser rate limit (NPT-WEB-2Y).
   const signOut = useCallback(async () => {
      // Don't change code order below
      setIsLoading(true);
      signedOutRef.current = true;

      const workosUser = getLocalStorage(WORKOS_USER_KEY) || null;
      try {
         if (workosUser && workosUser.id) {
            await signOutUser(workosUser.id);
         }
      } catch (err: any) {
         console.error('[Auth] Backend signOut failed', err);
      }

      clearUserSession();

      try {
         if (workosAuth.isLoading) {
            // AuthKit is still on its placeholder client (NPT-2099); the effect above signs out of
            // WorkOS once the real one arrives.
            pendingWorkOsSignOutRef.current = true;
         } else if (workosAuth) {
            // We do NOT use { navigate: false } so it redirects to WorkOS to clear their session cookie.
            // This ensures that when the user logs back in, they are prompted for credentials.
            await workosAuth.signOut();
         }
      } catch (err: any) {
         console.error('[Auth] WorkOS signOut failed', err);
      }

      setIsLoading(false);
   }, [workosAuth]);

   const handleSetCookie = async (organizationId: any) => {
      await setOrgCookie(organizationId);

      localStorage.setItem(ORGANIZATION_KEY, organizationId);
      setCurrentOrganizationId(organizationId);
      dispatch(orgAction.setOrgCookie(organizationId));
   };

   const clearUserSession = () => {
      clearLocalStorage();
      deleteCookie(TOKEN_KEY);
      setUser(null);
      setCurrentOrganizationId(null);
      // Start a fresh anonymous analytics session for the next visitor.
      resetUser();
      Sentry.setUser(null);
      // Reset LaunchDarkly back to a fresh anonymous context. We mint a
      // new key (rather than reusing any pre-login anonymous key) because
      // `clearLocalStorage()` above just wiped `npt_anon_id`. A brand-new
      // key is the correct post-logout state.
      if (ldClient) {
         const freshAnonKey = `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
         localStorage.setItem('npt_anon_id', freshAnonKey);
         ldClient.identify({ kind: 'user', key: freshAnonKey, anonymous: true }).catch((err: unknown) => {
            console.warn('[LD] identify (logout reset) failed', err);
         });
      }
   };

   return (
      <AuthContext.Provider
         value={{
            user,
            isAuthenticated,
            isAdmin,
            isDisabled,
            isLoading,
            isAuthRedirecting,
            isAuthProviderReady: !workosAuth.isLoading,
            isOrgAdmin,
            currentOrganizationId,
            refreshUser: fetchCurrentUser,
            setUser,
            signIn,
            signUp,
            signOut,
         }}
      >
         {children}
      </AuthContext.Provider>
   );
};

// eslint-disable-next-line react-refresh/only-export-components -- co-located with its provider; splitting would ripple through every import site
export const useAuth = () => {
   const context = useContext(AuthContext);
   if (!context) throw new Error('useAuth must be used within AuthProvider');

   return context;
};
